import { feature } from '@turf/turf';
import { FeatureCollection, Feature, Geometry, GeoJsonProperties } from 'geojson';
import _ from 'lodash';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import ReactPaginate from 'react-paginate';
import { generate } from 'short-uuid';
import { useFetch, FetchRequestTypes } from '../hooks/useFetch';
import LinerServiceRouteList, { LocalStorageRoute, Route } from './linerServiceAdminRoute';
import { LinerServiceDeleteDialogData } from './linerServiceDeleteDialog';

import BaseTable, { IBaseTableData, IBaseTableHeader, IBaseTableRow } from './table/baseTable';

export interface LinerServiceAdminProps {}

export interface LinerMapData {
  agents: Agent[];
  shippingCompanies: ShippingCompany[];
  terminals: Terminal[];
}

export interface Agent {
  id: string;
  name: string;
  link: string;
}

export interface ShippingCompany {
  id: string;
  name: string;
  link: string;
}

export interface Terminal {
  id: string;
  name: string;
  link: string;
}

export interface CityName {
  locale: string;
  name: string;
}

export interface City {
  id: string;
  names: CityName[];
}

export interface RouteParts {
  agent?: string;
  shippingCompany?: string;
  routeFrequency?: string;
  terminal?: string;
  cargo?: string;
}

export interface LinerServiceAdminSettings {
  localMapFeatures: Feature<Geometry, GeoJsonProperties>[];
  setLocalMapFeatures: Dispatch<SetStateAction<Feature<Geometry, GeoJsonProperties>[]>>;
  locale: string;
  setLocale: React.Dispatch<React.SetStateAction<string>>;
}

export const LinerServiceAdminContext = React.createContext<LinerServiceAdminSettings>({
  localMapFeatures: [],
  setLocalMapFeatures: () => {},
  locale: 'en',
  setLocale: () => {},
});

const TableHeader = (headerText: string, className?: string) => (
  <span className={className}>{`Namn (${headerText})`}</span>
);
const TableColumn = (headerText: string, className?: string) => <span className={className}>{headerText}</span>;

// ----------------------------------------
//  LinerServiceAdmin component
// ----------------------------------------

const LinerServiceAdmin = ({}: LinerServiceAdminProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const [deleteDialogData, setDeleteDialogData] = useState<LinerServiceDeleteDialogData>({
    deletePosX: 0,
    deletePosY: 0,
    onDeleteConfirmation: () => {},
  });

  const [fetchUrl, setFetchUrl] = useState<string[] | null>(null);
  const [putUrls, setPutUrls] = useState<string[] | null>(null);
  const [putRequestData, setPutRequestData] = useState<any[] | null>(null);

  const [mapboxMapFeatures, setMapboxMapFeatures] = useState<Feature<Geometry, GeoJsonProperties>[]>([]);
  const [localMapFeatures, setLocalMapFeatures] = useState<Feature<Geometry, GeoJsonProperties>[]>([]);

  const [filteredLocalMapFeatures, setFilteredLocalMapFeatures] = useState<Feature<Geometry, GeoJsonProperties>[]>([]);

  const [locale, setLocale] = useState<string>('en');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [shippingCompanies, setShippingCompanies] = useState<ShippingCompany[]>([]);
  const [terminals, setTerminals] = useState<Terminal[]>([]);

  // Check for faulty configuration in mapbox data
  const [checkMapboxData, setCheckMapboxData] = useState<( string | number | undefined)[]>([])

  // We start with an empty list of items.
  const [paginatedList, setPaginatedList] = useState<Feature<Geometry, GeoJsonProperties>[]>([]);
  const [pageCount, setPageCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);
  const [pageSelected, setPageSelected] = useState(0);

  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [showSentModal, setShowSentModal] = useState(false);

  const { data: linerMapData } = useFetch(fetchUrl, FetchRequestTypes.GET);
  const { data: putFeatureDataResponse } = useFetch(putUrls, FetchRequestTypes.PUT, putRequestData);

  // ---------------------------------------------------------------
  //  USE EFFECT - Mount - fetch data from MapBox and Optimizely
  // ---------------------------------------------------------------

  useEffect(() => {
    const urls = [
      `/api-mapboxdataset/cljmkiswq1t2r2cl7algc96db/features?access_token=pk.eyJ1IjoicG9ydGdvdGNvbSIsImEiOiJjbGhrN3F5dGUwNWVoM2VueG51cW5uaTJpIn0.Wp4hSyeLAqbD5dYBbniUAw`,
      `/api/linermapdata`,
      `/api-mapboxtileset`,
    ];
    setFetchUrl(urls);
  }, []);

  // -----------------------------------------------------------------------------------------
  //  USE EFFECT - linerMapData - data has been loaded set admin list and properties
  // -----------------------------------------------------------------------------------------

  useEffect(() => {
    if (linerMapData) {
      const mapboxData = linerMapData[0] as FeatureCollection;
      const features: Feature<Geometry, GeoJsonProperties>[] = mapboxData.features;

      setMapboxMapFeatures(features);

      const { agents, shippingCompanies, terminals }: LinerMapData = linerMapData[1];

      setAgents(agents);
      setShippingCompanies(shippingCompanies);
      setTerminals(terminals);
    }
  }, [linerMapData]);

  // ---------------------------------------------------------------
  //  USE MEMO - Cities - listen for localMapFeatures change and
  //                      create a citylist for dropdown
  // ---------------------------------------------------------------

  const cities: City[] = useMemo(() => {
    if (mapboxMapFeatures?.length) {
      const featureCity: City[] = mapboxMapFeatures.map((feature) => {
        const properties = feature.properties as any;
        const names: CityName[] = [];
        for (const key in feature.properties) {
          if (key.includes('name')) {
            const splittedKey = key.split('_');
            names.push({ locale: splittedKey[splittedKey.length - 1], name: feature.properties[key] });
          }
        }

        return { id: properties.nodeId, names: names };
      });

      return featureCity;
    }
    return [];
  }, [mapboxMapFeatures]);

  // ---------------------------------------------------------------
  //  sortList - sort the feature list on city name if exists
  // ---------------------------------------------------------------

  const sortList = (list: Feature<Geometry, GeoJsonProperties>[]) => {
    //has name
    const listHasName = list.some(
      (feature) =>
        (feature.properties as any).hasOwnProperty('name_sv') || (feature.properties as any).hasOwnProperty('name_en')
    );

    //If nothing to sort on then
    if (!listHasName) {
      return list;
    }
    // ... else sort on name
    return list.sort((a, b) => {
      const sortA = (a.properties as any)['name_sv'] || (a.properties as any)['name_en'];
      const sortB = (b.properties as any)['name_sv'] || (b.properties as any)['name_en'];
      if (sortA && sortB) {
        if (sortA < sortB) {
          return -1;
        }
        if (sortA > sortB) {
          return 1;
        }
      }

      return 0;
    });
  };

  // -----------------------------------------------------------------------------------------
  // USE EFFECT - mapboxMapFeatures - mapbox data loaded set local data
  // -----------------------------------------------------------------------------------------

  useEffect(() => {
    if (mapboxMapFeatures) {
      const features = _.cloneDeep(mapboxMapFeatures);
      const sortedFeatures = sortList(features);
      syncLocalStorage(sortedFeatures);
      setLocalMapFeatures([...sortedFeatures]);
      setFilteredLocalMapFeatures([...sortedFeatures]);
    }
  }, [mapboxMapFeatures]);

  // -----------------------------------------------------------------------------------------
  // syncLocalStorage - syncs the routes data between local storage and mapbox
  // -----------------------------------------------------------------------------------------

  const syncLocalStorage = (features: Feature<Geometry, GeoJsonProperties>[]) => {
    if (features?.length) {
      // Get local storage data
      const localStorageData = localStorage.getItem('ghabRoutes');
      const localStorageRoutes: LocalStorageRoute[] = localStorageData ? JSON.parse(localStorageData) : [];
      //Compare between localStorage and MapBox Routes
      localStorageRoutes.forEach((localStorageRoute) => {
        const matchedExternalFeature = features.find(
          (feature) => feature.properties?.nodeId === localStorageRoute.route.nodeId
        );

        if (matchedExternalFeature?.properties && !matchedExternalFeature?.properties?.routes) {
          matchedExternalFeature.properties.routes = [];
        }

        const externalRoutes: Route[] = matchedExternalFeature?.properties?.routes as Route[];

        const externalRouteIndex = externalRoutes?.findIndex((extRoute) => extRoute.id === localStorageRoute.route.id);

        if (externalRouteIndex >= 0) {
          if (localStorageRoute.removed) {
            externalRoutes.splice(externalRouteIndex, 1);
          } else {
            if (!_.isEqual(localStorageRoute.route, externalRoutes[externalRouteIndex])) {
              externalRoutes[externalRouteIndex] = localStorageRoute.route;
            }
          }
        } else if (localStorageRoute?.route && !localStorageRoute.removed) {
          externalRoutes.push(localStorageRoute.route);
        }
      });
    }
  };

  // --------------------------------------------------------------------------------------------------------
  //  USE MEMO - citiesTableData - creates data for the admin table whenever localMapFeatures is updated
  // --------------------------------------------------------------------------------------------------------

  const citiesTableData = useMemo(() => {
    const baseData: IBaseTableData = {
      headers: [],
      rows: [],
    };
    paginatedList.forEach((feature, index) => {
      const props: any = {
        id: generate(),
        feature,
        agents,
        shippingCompanies,
        terminals,
        cities,
        mapboxMapFeatures,
      };

      const rowData: IBaseTableRow = {
        id: props.id || generate(),
        columns: [],
        expandedContent: <LinerServiceRouteList {...props} />,
        expandedAtStart: false,
      };

      const city = cities.find(({ id }) => id === (feature.properties as any).nodeId);

      city?.names.forEach((name, i) => {
        rowData.columns.push({
          id: i,
          columnData: TableColumn(name.name),
          filterValue: name.name,
        });
      });

      baseData.rows.push(rowData);
    });

    let longestCityNameList: City = { id: '', names: [] };
    cities.forEach((city) => {
      if (city.names.length > longestCityNameList.names.length) {
        longestCityNameList = city;
      }
    });
    if (longestCityNameList.names.length) {
      const headerNames: CityName[] = longestCityNameList.names;
      headerNames.forEach((headerName, index) => {
        const headerData: IBaseTableHeader = {
          id: index,
          headerData: TableHeader(headerName.locale),
          columnId: index,
        };
        baseData.headers.push(headerData);
      });
    }

    return baseData;
  }, [paginatedList]);

  useEffect(() => {
    if (putFeatureDataResponse) {
      setShowSentModal(true);

      const responseFeatures: Feature<Geometry, GeoJsonProperties>[] = putFeatureDataResponse;
      if (responseFeatures) {
        localStorage.removeItem('ghabRoutes');
      }
    }
  }, [putFeatureDataResponse]);

  // --------------------------------------------------------------------------------------------------------
  //  sendData  - sends updated feature data to MapBox
  // --------------------------------------------------------------------------------------------------------

  const sendData = () => {
    const urls: string[] = [];
    const requestDataList: Feature<Geometry, GeoJsonProperties>[] = [];

    localMapFeatures.forEach((feature) => {
      urls.push(`/api-mapboxupdatedataset/${feature.id}`);
      requestDataList.push({
        type: 'Feature',
        id: feature.id,
        geometry: feature.geometry,
        properties: feature.properties,
      });
    });
    setPutRequestData([...requestDataList]);
      setPutUrls([...urls]);
  };
  
  useEffect(() => {
    if (filteredLocalMapFeatures) {
      const endOffset = itemOffset + itemsPerPage;
      setPaginatedList(filteredLocalMapFeatures.slice(itemOffset, endOffset));
      setPageCount(Math.ceil(filteredLocalMapFeatures.length / itemsPerPage));
      checkForErrorInMapboxData(filteredLocalMapFeatures);
      }
  }, [itemOffset, itemsPerPage, filteredLocalMapFeatures]);

    const checkForErrorInMapboxData = (filteredLocalMapFeatures: Feature<Geometry, GeoJsonProperties>[]) => {
      setCheckMapboxData([]);
      if (filteredLocalMapFeatures != null && filteredLocalMapFeatures.length > 0) { 
        filteredLocalMapFeatures.forEach((mapFeatures) => {
        let properties = mapFeatures.properties;    

          if (properties !== null) {
            let hasValidPropertiesValues =
            properties?.name_sv !== null && properties?.name_sv !== undefined && properties?.name_sv !== "" &&
            properties?.name_en !== null && properties?.name_en !== undefined && properties?.name_en !== "" &&
            properties?.nodeId !== null && properties?.nodeId !== undefined && properties?.nodeId !== "";

              if (!hasValidPropertiesValues) {
                if (properties?.name_sv !== null && properties?.name_sv !== undefined && properties?.name_sv !== "") {
                  setCheckMapboxData(prevItems => [...prevItems, properties?.name_sv])
                } else if (properties?.name_en !== null && properties?.name_en !== undefined && properties?.name_en !== "") {
                  setCheckMapboxData(prevItems => [...prevItems, properties?.name_en])
                } else {
                  setCheckMapboxData(prevItems => [...prevItems, mapFeatures.id])
                }
              }
          }

        })
      };
    };

  const handlePageClick = (event: any) => {
    if (filteredLocalMapFeatures) {
      setPageSelected(event.pageSelected);
      const newOffset = (event.selected * itemsPerPage) % filteredLocalMapFeatures.length;
      setItemOffset(newOffset);
    }
  };

  const searchList = (e: React.FormEvent<HTMLInputElement>) => {
    const searchValue = e.currentTarget.value;
    if (localMapFeatures) {
      const filteredCities = cities.filter((city) =>
        city.names.some(({ name }) => name.toLocaleLowerCase().includes(searchValue.toLowerCase()))
      );

      const filteredList = localMapFeatures.filter((feature) =>
        filteredCities.some((city) => city.id === (feature.properties as any).nodeId)
      );
      setItemOffset(0);
      setFilteredLocalMapFeatures(filteredList);
    }
  };

  const resetSearch = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (localMapFeatures) {
      const input = document.getElementById('liner-service-admin-search-input');
      input?.focus();
      setFilteredLocalMapFeatures(localMapFeatures);
    }
  };

  // --------------------------------------------------------------------------------------------------------
  // JSX
  // --------------------------------------------------------------------------------------------------------

  return (
    <LinerServiceAdminContext.Provider
      value={{
        localMapFeatures,
        setLocalMapFeatures,
        locale,
        setLocale,
      }}
    >
      <div ref={ref} className="liner-service-admin">
        {showSentModal ? (
          <div className="liner-service-admin-routes-sent-modal">
            <div className="modal-box">
              <h5>Data är nu uppladdat till MapBox. Använd MapBox-dataset editor för att publicera.</h5>
              <button className="confirm-button" type="button" onClick={() => setShowSentModal(false)}>
                <span>Ok</span>
              </button>
            </div>
            <div className="modal-backdrop" onClick={() => setShowSentModal(false)}></div>
          </div>
        ) : null}

        <div className="top-content">
          <div className="liner-service-admin-search">
            <form>
              <input
                id="liner-service-admin-search-input"
                className="ghab-input"
                type="text"
                required
                name="q"
                placeholder="Sök"
                onChange={searchList}
              />
              <button
                title="clear search input text"
                className="search-page-search-input-clear-text"
                type="reset"
                onClick={resetSearch}
              >
                <i className="bi bi-x-circle"></i>
              </button>
            </form>
            <button
              id="search-page-search-submit-button"
              type="submit"
              className="btn btn-icon submit-search"
              aria-label="Submit search query"
            >
              <img alt="search icon" src="/Assets/icons/search_icon.svg" />
            </button>
          </div>
          <button className="send-routes-button" type="button" onClick={sendData}>
            <span>Spara & skicka till MapBox</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              className="bi bi-cloud-arrow-up"
              viewBox="0 0 16 16"
            >
              <path
                fillRule="evenodd"
                d="M7.646 5.146a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1-.708.708L8.5 6.707V10.5a.5.5 0 0 1-1 0V6.707L6.354 7.854a.5.5 0 1 1-.708-.708l2-2z"
              />
              <path d="M4.406 3.342A5.53 5.53 0 0 1 8 2c2.69 0 4.923 2 5.166 4.579C14.758 6.804 16 8.137 16 9.773 16 11.569 14.502 13 12.687 13H3.781C1.708 13 0 11.366 0 9.318c0-1.763 1.266-3.223 2.942-3.593.143-.863.698-1.723 1.464-2.383zm.653.757c-.757.653-1.153 1.44-1.153 2.056v.448l-.445.049C2.064 6.805 1 7.952 1 9.318 1 10.785 2.23 12 3.781 12h8.906C13.98 12 15 10.988 15 9.773c0-1.216-1.02-2.228-2.313-2.228h-.5v-.5C12.188 4.825 10.328 3 8 3a4.53 4.53 0 0 0-2.941 1.1z" />
            </svg>
          </button>
        </div>
        {checkMapboxData && checkMapboxData.length > 0 && 
          <div>
            <p>
              Hoppsan! Något blev fel eller saknas för att kunna publicera detta på rätt sätt.
              Var noga med stora och små bokstäver i fälten name_sv, name_en och nodeId.
            </p>
            <ul>
              {checkMapboxData.map((x, index) => (
                <li key={index}> {x} </li>
              ))}
            </ul>
          </div>
        }
              
        <BaseTable data={citiesTableData} />

        <ReactPaginate
          nextLabel="next >"
          onPageChange={handlePageClick}
          pageRangeDisplayed={3}
          marginPagesDisplayed={2}
          pageCount={pageCount}
          previousLabel="< previous"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="page-item"
          previousLinkClassName="page-link"
          nextClassName="page-item"
          nextLinkClassName="page-link"
          breakLabel="..."
          breakClassName="page-item"
          breakLinkClassName="page-link"
          containerClassName="pagination"
          activeClassName="active"
          forcePage={itemOffset <= 0 ? 0 : undefined}
        />
      </div>
    </LinerServiceAdminContext.Provider>
  );
};

export default LinerServiceAdmin;

